import { UnorderedListOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { Breakpoints, TagInfo } from "@stepfinance/react";
import type { RadioChangeEvent } from "antd";
import { Radio } from "antd";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { ReactComponent as HistoryIcon } from "../../assets/history.svg";
import { ConnectView } from "../../components/ConnectView/ConnectView";
import { StepAuth } from "../../components/StepAuth";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useLanguageContext } from "../../contexts/language/language";
import { useNavigationContext } from "../../contexts/navigation";
import { ViewHeader } from "..";
import { CalendarHistoryView } from "./calendar";
import { LatestHistoryView } from "./latest";

interface TransactionHistoryViewProps {
  type: "latest" | "calendar";
}

export const TransactionHistoryView: FC<TransactionHistoryViewProps> = ({ type }) => {
  const { connected, watching } = useEnrichedWallet();
  const { navigate } = useNavigationContext();
  const [selectedType, setSelectedType] = useState<TransactionHistoryViewProps["type"]>(type);
  const location = useLocation();

  const {
    currentLanguageData: {
      pages: { transactionHistory },
    },
  } = useLanguageContext();

  const handleSwitchPageClick = (event: RadioChangeEvent) => {
    navigate("/history/" + event.target.value);
  };

  useEffect(() => {
    const type = location.pathname.split("/")[3] as TransactionHistoryViewProps["type"];
    if ((type && type === "calendar") || type === "latest") {
      setSelectedType(watching ? "latest" : type);
    }
  }, [location.pathname, watching]);

  return connected ? (
    <div style={{ width: "100%" }}>
      <ViewHeader>
        <HeaderContainer>
          <TitleContainer>
            <TextWithIcon>
              <HistoryIcon width={32} height={32} style={{ marginRight: 10 }} />
              <span className="title">{transactionHistory.title}</span>
            </TextWithIcon>
          </TitleContainer>
          <Radio.Group
            size="large"
            className="radio-group-container"
            onChange={handleSwitchPageClick}
            value={selectedType}
          >
            <Radio.Button className="latest-button" value="latest">
              <TextWithIcon>
                <HistoryIcon width={14} height={14} style={{ marginRight: 8 }} />
                {transactionHistory.latest}
              </TextWithIcon>
            </Radio.Button>
            <Radio.Button className="calendar-button" value="calendar" disabled={watching}>
              <TextWithIcon>
                <UnorderedListOutlined style={{ marginRight: 8 }} />
                {transactionHistory.allTransactions}
                <div className="new-container">
                  <TagInfo tagType="text" uppercase={false} useSpaceMono={false}>
                    New!
                  </TagInfo>
                </div>
              </TextWithIcon>
            </Radio.Button>
          </Radio.Group>
        </HeaderContainer>
      </ViewHeader>
      {selectedType === "latest" || watching ? (
        <LatestHistoryView />
      ) : (
        <StepAuth>
          <CalendarHistoryView />
        </StepAuth>
      )}
    </div>
  ) : (
    <ConnectView />
  );
};

const TitleContainer = styled.div`
  flex: 1;
  margin-right: 16px;
  .title {
    white-space: nowrap;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  .radio-group-container {
    .latest-button {
      flex: none;
      width: 130px;
    }
    width: 380px;
    display: flex;
    justify-content: flex-end;
    .new-container {
      margin-left: 8px;
      display: flex;
      align-items: center;
    }
    .radio-button {
      flex: 1;
    }
    @media (max-width: ${Breakpoints.S}px) {
      width: 100%;
      flex: 1;
      .latest-button {
        flex: 1;
      }
      .calendar-button {
        .flex: 1;
      }
    }
  }
`;

const TextWithIcon = styled.div`
  display: flex;
  align-items: center;
`;

export default TransactionHistoryView;
